import { useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import toast from "react-hot-toast"
import Input from "components/forms/input"
import ErrorMessage from "components/forms/error-message"

// 220726 (permintaan user): Faktur Umum kini input nama barang BY TEXT — picker
// katalog DIHAPUS TOTAL, product_unit_id tidak lagi dipakai. Baris item di-key
// dengan _uid lokal (bukan product_unit_id) karena item murni manual.

function todayISO() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`
}

function num(v) {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

// DECIMAL(18,3) dari backend ("1.000") → tampil "1", "2.500" → "2,5"
function formatQty(v) {
  return num(v).toLocaleString("id-ID", { maximumFractionDigits: 3 })
}

function formatIdr(v) {
  return num(v).toLocaleString("id-ID", { maximumFractionDigits: 0 })
}

const emptyItem = (uid) => ({
  _uid: uid,
  product_unit_name: "",
  qty: 1,
  uom: "PCS",
  // Faktur = harga wajib; kosong ("") ditolak saat submit.
  price: "",
  notes: "",
})

export default function Form({ data, errors = {}, onSubmit, submitting }) {
  const navigate = useNavigate()
  const isEdit = Boolean(data?.id)

  const uidRef = useRef(1)
  const nextUid = () => `row-${uidRef.current++}`

  const [docNumber, setDocNumber] = useState(data?.doc_number ?? "")
  const [docDate, setDocDate] = useState(data?.doc_date ?? todayISO())
  const [fromParty, setFromParty] = useState(data?.from_party ?? "")
  const [toParty, setToParty] = useState(data?.to_party ?? "")
  const [notes, setNotes] = useState(data?.notes ?? "")

  const [items, setItems] = useState(() =>
    Array.isArray(data?.items) && data.items.length > 0
      ? data.items.map((r) => ({
          _uid: nextUid(),
          product_unit_name: r.product_label ?? r.product_unit_name ?? r.product_unit_id ?? "",
          qty: r.qty != null ? num(r.qty) : 1,
          uom: r.uom ?? "PCS",
          price: r.price != null ? num(r.price) : "",
          notes: r.notes ?? "",
        }))
      : [emptyItem(nextUid())]
  )

  const totalQty = useMemo(() => items.reduce((acc, r) => acc + num(r.qty), 0), [items])
  const totalAmount = useMemo(
    () => items.reduce((acc, r) => acc + num(r.qty) * num(r.price), 0),
    [items]
  )

  const onAddRow = () => setItems((prev) => [...prev, emptyItem(nextUid())])
  const onUpdateItem = (uid, patch) =>
    setItems((prev) => prev.map((it) => (it._uid === uid ? { ...it, ...patch } : it)))
  const onRemoveItem = (uid) => setItems((prev) => prev.filter((it) => it._uid !== uid))

  const buildPayload = () => ({
    doc_number: (docNumber || "").trim() || null,
    doc_date: docDate,
    from_party: (fromParty || "").trim() || null,
    to_party: (toParty || "").trim() || null,
    notes: (notes || "").trim() || null,
    // Item tanpa nama barang dibuang; product_unit_id selalu null (murni teks).
    items: items
      .filter((it) => (it.product_unit_name || "").trim())
      .map((it) => ({
        product_unit_id: null,
        product_unit_name: (it.product_unit_name || "").trim(),
        qty: num(it.qty),
        uom: (it.uom || "PCS").trim() || "PCS",
        price: it.price === "" || it.price == null ? 0 : num(it.price),
        notes: (it.notes || "").trim() || null,
      })),
  })

  const handleSubmit = () => {
    const valid = items.filter((it) => (it.product_unit_name || "").trim())
    if (valid.length === 0) {
      toast.error("Tambahkan minimal 1 item (isi nama barang)")
      return
    }
    const hasEmptyPrice = valid.some((it) => it.price === "" || it.price == null)
    if (hasEmptyPrice) {
      toast.error("Isi harga untuk semua item")
      return
    }
    onSubmit?.(buildPayload())
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="p-4 space-y-4 bg-white border rounded-2xl border-neutral-200">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div>
            <label className="block text-xs text-neutral-700">No. Faktur</label>
            <Input
              value={docNumber}
              onChange={(e) => setDocNumber(e.target.value)}
              placeholder="Kosongkan untuk nomor otomatis"
            />
            <ErrorMessage error={errors?.doc_number} />
          </div>

          <div>
            <label className="block text-xs text-neutral-700">Tanggal</label>
            <Input type="date" value={docDate} onChange={(e) => setDocDate(e.target.value)} />
            <ErrorMessage error={errors?.doc_date} />
          </div>

          <div>
            <label className="block text-xs text-neutral-700">Dari (penerbit)</label>
            <Input value={fromParty} onChange={(e) => setFromParty(e.target.value)} placeholder="mis. Toko Atalla" />
            <ErrorMessage error={errors?.from_party} />
          </div>

          <div>
            <label className="block text-xs text-neutral-700">Kepada (pelanggan)</label>
            <Input value={toParty} onChange={(e) => setToParty(e.target.value)} placeholder="mis. PT. Contoh Jaya" />
            <ErrorMessage error={errors?.to_party} />
          </div>

          <div className="md:col-span-3">
            <label className="block text-xs text-neutral-700">Catatan</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Keterangan tambahan (opsional)"
              rows={3}
              className="block w-full px-4 py-2 mt-1 text-sm transition border border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200"
            />
          </div>
        </div>
      </div>

      {/* Item — input nama barang bebas (teks) */}
      <div className="p-4 space-y-4 bg-white border rounded-2xl border-neutral-200">
        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold text-neutral-800">Daftar Barang</div>
          <button
            type="button"
            onClick={onAddRow}
            className="px-4 py-2 text-sm font-semibold text-white bg-brand-900 hover:bg-brand-800 rounded-xl"
          >
            + Tambah Baris
          </button>
        </div>
        <ErrorMessage error={errors?.items} />

        <div className="overflow-x-auto border border-neutral-200 rounded-xl">
          <table className="min-w-full divide-y divide-neutral-200">
            <thead className="bg-neutral-50">
              <tr>
                <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500">Nama Barang</th>
                <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500">Satuan</th>
                <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500">Qty</th>
                <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500">Harga</th>
                <th className="px-3 py-3 text-xs font-medium text-right uppercase text-neutral-500">Jumlah</th>
                <th className="px-3 py-3 text-xs font-medium text-left uppercase text-neutral-500">Catatan</th>
                <th className="px-3 py-3 text-xs font-medium text-right uppercase text-neutral-500">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {items.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-3 py-6 text-sm text-center text-neutral-500">
                    Belum ada item. Klik "Tambah Baris" lalu ketik nama barang.
                  </td>
                </tr>
              ) : (
                items.map((it) => (
                  <tr key={it._uid}>
                    <td className="px-3 py-2 align-top">
                      <input
                        value={it.product_unit_name}
                        onChange={(e) => onUpdateItem(it._uid, { product_unit_name: e.target.value })}
                        placeholder="Nama barang"
                        className="w-56 px-2 py-1 text-sm border rounded-lg border-neutral-200"
                      />
                    </td>
                    <td className="px-3 py-2 align-top">
                      <input
                        value={it.uom}
                        onChange={(e) => onUpdateItem(it._uid, { uom: e.target.value })}
                        className="w-20 px-2 py-1 text-sm border rounded-lg border-neutral-200"
                      />
                    </td>
                    <td className="px-3 py-2 align-top">
                      <input
                        type="number"
                        min={0}
                        value={it.qty}
                        onChange={(e) => onUpdateItem(it._uid, { qty: e.target.value })}
                        className="w-24 px-2 py-1 text-sm border rounded-lg border-neutral-200"
                      />
                    </td>
                    <td className="px-3 py-2 align-top">
                      <input
                        type="number"
                        min={0}
                        placeholder="0"
                        value={it.price}
                        onChange={(e) => onUpdateItem(it._uid, { price: e.target.value })}
                        className="w-28 px-2 py-1 text-sm border rounded-lg border-neutral-200"
                      />
                    </td>
                    <td className="px-3 py-2 text-sm text-right align-top whitespace-nowrap">
                      {formatIdr(num(it.qty) * num(it.price))}
                    </td>
                    <td className="px-3 py-2 align-top">
                      <input
                        value={it.notes}
                        onChange={(e) => onUpdateItem(it._uid, { notes: e.target.value })}
                        className="w-full px-2 py-1 text-sm border rounded-lg border-neutral-200"
                      />
                    </td>
                    <td className="px-3 py-2 text-right align-top">
                      <button
                        type="button"
                        onClick={() => onRemoveItem(it._uid)}
                        className="text-xs font-semibold text-red-600 hover:underline"
                      >
                        Hapus
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            <tfoot className="bg-neutral-50">
              <tr>
                <td className="px-3 py-2 text-xs font-semibold text-right uppercase" colSpan={2}>
                  Total
                </td>
                <td className="px-3 py-2 text-sm font-semibold">{formatQty(totalQty)}</td>
                <td className="px-3 py-2 text-xs font-semibold text-right uppercase">Grand Total</td>
                <td className="px-3 py-2 text-sm font-semibold text-right whitespace-nowrap">
                  Rp {formatIdr(totalAmount)}
                </td>
                <td colSpan={2}></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <div className="flex items-center justify-end gap-3">
        <button
          type="button"
          onClick={() => navigate("/faktur-umum")}
          className="px-5 py-2 text-sm font-semibold border rounded-xl border-neutral-300 text-neutral-700"
        >
          Batal
        </button>
        <button
          type="button"
          disabled={submitting}
          onClick={handleSubmit}
          className={`px-5 py-2 text-sm font-semibold text-white rounded-xl ${
            submitting ? "bg-neutral-400 cursor-not-allowed" : "bg-brand-900 hover:bg-brand-800"
          }`}
        >
          {isEdit ? "Simpan Perubahan" : "Simpan"}
        </button>
      </div>
    </div>
  )
}
